/**
 * V91-3-2: Control spec extraction service — BYOP (Bring Your Own Policy).
 *
 * LLM-assisted extraction of machine-readable control evaluation criteria
 * from uploaded policy documents (PDF, markdown, text). Produces a draft
 * ControlSpecExtraction that pre-fills the review form for owner/admin
 * approval. Confidence gating per PRD §8.3.
 *
 * Pure function — no DB access, no platform deps, testable with a test
 * gateway. The LLM call goes through the egress gateway (ADR-0073): the
 * policy document is tenant-class; a refusal (federal mode without an
 * eligible provider) throws LlmRefusedError and the route answers with the
 * parity action (enter the criteria by hand).
 */
import { defineTemplate, field } from '../egress/data-classes.js';
import { LlmRefusedError } from '../egress/gateway.js';
// ---- Prompt constants (snapshot-stable) -----------------------------------
const VALID_VERDICT_POLICIES = new Set(['all_must_pass', 'any', 'weighted']);
/**
 * Instruction prompt for LLM extraction. Kept as a const so tests can
 * snapshot the exact prompt — changes to this string ARE product changes.
 */
export const EXTRACTION_INSTRUCTIONS = [
    'You are extracting machine-readable control evaluation criteria from a policy document.',
    '',
    'CONTROL: {title}',
    'DESCRIPTION: {description}',
    'APPLICABLE FRAMEWORKS: {framework_refs}',
    '',
    'DOCUMENT:',
    '{document_text}',
    '',
    'Extract as JSON:',
    '{',
    '  "accepted_signals": ["signal_name_1", "signal_name_2"],',
    '  "threshold": {},',
    '  "freshness_seconds": <number | null>,',
    '  "verdict_policy": "all_must_pass" | "any" | "weighted",',
    '  "rationale": "One-paragraph explanation of why these signals and thresholds apply to this control based on the document.",',
    '  "confidence": <0.0-1.0>,',
    '  "source_excerpts": ["Exact quote from the document supporting each extraction decision."]',
    '}',
    '',
    'Rules:',
    '- accepted_signals must be lowercase_snake_case identifiers describing evidence types (e.g., "encryption_at_rest", "access_review_log", "vulnerability_scan_report").',
    '- threshold should contain numeric gates where the document specifies quantitative criteria (e.g., {"max_days_between_reviews": 90}). Leave as {} if no numeric criteria found.',
    '- freshness_seconds is how often evidence must be refreshed. Use null if the document does not specify.',
    '- verdict_policy: use "all_must_pass" unless the document explicitly allows partial compliance.',
    '- confidence: 1.0 = document explicitly addresses this control with clear criteria; 0.5 = inferred from related language; 0.0 = no relevant content found.',
    '- source_excerpts: include 1-3 exact quotes from the document that support your extraction.',
    '- If the document does not address this control, return confidence 0.0 with empty accepted_signals.',
].join('\n');
export const EXTRACTION_SCHEMA = {
    accepted_signals: ['string'],
    threshold: {},
    freshness_seconds: 'number | null',
    verdict_policy: 'all_must_pass | any | weighted',
    rationale: 'string',
    confidence: 'number (0.0-1.0)',
    source_excerpts: ['string'],
};
// ---- Helpers -------------------------------------------------------------
/** ADR-0073: the framing text with named slots (control fields are reference; the document is tenant). */
export const EXTRACTION_INPUT_TEMPLATE = defineTemplate('control_spec.extract.input', 'v1', EXTRACTION_INSTRUCTIONS, ['title', 'description', 'framework_refs', 'document_text']);
export const EXTRACTION_TEMPLATE = defineTemplate('control_spec.extract', 'v1', 'Extract control evaluation criteria as JSON.', []);
function promptSlots(input) {
    return {
        title: field('register.control_text', input.control_title),
        description: field('register.control_text', input.control_description || '(none)'),
        framework_refs: field('register.control_text', input.framework_refs.join(', ') || '(none)'),
        document_text: field('policy_doc.text', truncateDocument(input.document_text)),
    };
}
/**
 * Truncate document text to a reasonable size for LLM context.
 * Most policy documents relevant to a single control are well under this.
 */
function truncateDocument(text, maxChars = 50_000) {
    if (text.length <= maxChars)
        return text;
    return text.slice(0, maxChars) + '\n\n[... document truncated ...]';
}
/**
 * Parse and validate the LLM response. Gracefully handles malformed
 * responses — returns low confidence with empty fields rather than throwing.
 */
function normalizeExtraction(controlId, raw) {
    const empty = {
        control_id: controlId,
        accepted_signals: [],
        threshold_suggestions: {},
        freshness_suggestion_seconds: null,
        verdict_policy: 'all_must_pass',
        rationale: '',
        confidence: 0,
        source_excerpts: [],
    };
    if (!raw || typeof raw !== 'object')
        return empty;
    const data = raw;
    // accepted_signals: array of strings, lowercase_snake_case
    const signals = [];
    if (Array.isArray(data.accepted_signals)) {
        for (const s of data.accepted_signals) {
            if (typeof s === 'string' && s.trim()) {
                signals.push(s.trim().toLowerCase().replace(/[\s-]+/g, '_'));
            }
        }
    }
    // threshold: plain object
    let threshold = {};
    if (data.threshold && typeof data.threshold === 'object' && !Array.isArray(data.threshold)) {
        threshold = data.threshold;
    }
    // freshness_seconds: number or null
    let freshness = null;
    if (typeof data.freshness_seconds === 'number' && data.freshness_seconds > 0) {
        freshness = Math.round(data.freshness_seconds);
    }
    // verdict_policy
    let verdict = 'all_must_pass';
    if (typeof data.verdict_policy === 'string' && VALID_VERDICT_POLICIES.has(data.verdict_policy)) {
        verdict = data.verdict_policy;
    }
    // rationale
    const rationale = typeof data.rationale === 'string' ? data.rationale.trim() : '';
    // confidence: clamp to 0-1
    let confidence = 0;
    if (typeof data.confidence === 'number' && isFinite(data.confidence)) {
        confidence = Math.max(0, Math.min(1, data.confidence));
    }
    // source_excerpts: array of strings
    const excerpts = [];
    if (Array.isArray(data.source_excerpts)) {
        for (const e of data.source_excerpts) {
            if (typeof e === 'string' && e.trim()) {
                excerpts.push(e.trim());
            }
        }
    }
    return {
        control_id: controlId,
        accepted_signals: signals,
        threshold_suggestions: threshold,
        freshness_suggestion_seconds: freshness,
        verdict_policy: verdict,
        rationale,
        confidence,
        source_excerpts: excerpts,
    };
}
// ---- Main extraction function --------------------------------------------
/**
 * Extract control spec criteria from an uploaded policy document using
 * an LLM provider. Uses `extract` if available, falls back to `synthesize`.
 *
 * Returns a draft extraction with confidence scoring. The caller (route
 * handler) returns this to the SPA for review-form pre-fill.
 */
export async function extractControlSpec(input, llm) {
    let result;
    try {
        result = await llm.run({
            task: 'control_spec.extract', template: EXTRACTION_TEMPLATE, inputTemplate: EXTRACTION_INPUT_TEMPLATE,
            slots: promptSlots(input), schemaHint: EXTRACTION_SCHEMA,
        });
    }
    catch {
        // Provider error — return empty extraction with zero confidence (the reviewer fills it in).
        return normalizeExtraction(input.control_id, null);
    }
    // Refused (federal mode / raised class, no eligible provider): parity off_with_action.
    if (result.status !== 'ok')
        throw new LlmRefusedError(result);
    const extraction = normalizeExtraction(input.control_id, result.output);
    // Use the provider's confidence if ours is zero (document not relevant)
    if (extraction.confidence === 0 && result.confidence > 0) {
        extraction.confidence = Math.min(result.confidence, 0.3);
    }
    return extraction;
}
/**
 * Generate test fixtures from an extraction result.
 * Called by the propose-spec endpoint when `source_kind = 'customer_upload'`
 * to include smoke-test fixtures in the proposed_changes row.
 */
export function generateTestFixtures(extraction) {
    return [
        {
            label: 'pass — all signals present',
            evidence: Object.fromEntries(extraction.accepted_signals.map((s) => [
                s,
                { present: true, timestamp: new Date().toISOString() },
            ])),
            expected: 'pass',
        },
        {
            label: 'fail — missing required signal',
            evidence: extraction.accepted_signals.length > 1
                ? Object.fromEntries(extraction.accepted_signals.slice(1).map((s) => [
                    s,
                    { present: true },
                ]))
                : {},
            expected: 'fail',
        },
        {
            label: 'insufficient — no evidence',
            evidence: {},
            expected: 'insufficient',
        },
    ];
}
//# sourceMappingURL=control-spec-extraction-service.js.map